import { useState, useEffect, useRef, useCallback } from "react";
import { searchWeb, openExternal } from "@/lib/api";

// ─── Snake ───────────────────────────────────────────────────────────────────
const COLS = 26;
const ROWS = 18;
const CELL = 17;

function SnakeGame({ onBack }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const loopRef  = useRef(null);
  const [score, setScore]   = useState(0);
  const [phase, setPhase]   = useState("idle");

  const initState = () => ({
    snake:   [{ x: 13, y: 9 }, { x: 12, y: 9 }, { x: 11, y: 9 }],
    dir:     { x: 1, y: 0 },
    nextDir: { x: 1, y: 0 },
    food:    { x: 20, y: 9 },
    score:   0,
  });

  const randomFood = (snake) => {
    let p;
    do { p = { x: Math.floor(Math.random() * COLS), y: Math.floor(Math.random() * ROWS) }; }
    while (snake.some(s => s.x === p.x && s.y === p.y));
    return p;
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const s = stateRef.current;

    ctx.fillStyle = "#0d0d0d";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#1a1a1a";
    for (let x = 0; x < COLS; x++)
      for (let y = 0; y < ROWS; y++)
        ctx.fillRect(x * CELL + 8, y * CELL + 8, 1, 1);

    if (!s) return;

    s.snake.forEach((seg, i) => {
      const isHead = i === 0;
      ctx.fillStyle = isHead ? "#e63946" : i % 2 === 0 ? "#c1121f" : "#d62839";
      ctx.fillRect(seg.x * CELL + 2, seg.y * CELL + 2, CELL - 4, CELL - 4);
      if (isHead) {
        ctx.fillStyle = "#f1faee";
        const ex = s.dir.x > 0 ? 10 : s.dir.x < 0 ? 2 : 5;
        const ey1 = s.dir.y > 0 ? 10 : s.dir.y < 0 ? 3 : 4;
        ctx.fillRect(seg.x * CELL + ex, seg.y * CELL + ey1, 2, 2);
        ctx.fillRect(seg.x * CELL + ex, seg.y * CELL + ey1 + 5, 2, 2);
      }
    });

    ctx.fillStyle = "#f1faee";
    ctx.fillRect(s.food.x * CELL + 3, s.food.y * CELL + 3, CELL - 6, CELL - 6);
    ctx.fillStyle = "#e63946";
    ctx.fillRect(s.food.x * CELL + 5, s.food.y * CELL + 5, CELL - 10, CELL - 10);
  }, []);

  const tick = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    s.dir = { ...s.nextDir };
    const head = { x: s.snake[0].x + s.dir.x, y: s.snake[0].y + s.dir.y };
    if (head.x < 0 || head.x >= COLS || head.y < 0 || head.y >= ROWS ||
        s.snake.some(seg => seg.x === head.x && seg.y === head.y)) {
      clearInterval(loopRef.current);
      loopRef.current = null;
      setPhase("over");
      setScore(s.score);
      draw();
      return;
    }
    s.snake.unshift(head);
    if (head.x === s.food.x && head.y === s.food.y) {
      s.score += 10;
      s.food = randomFood(s.snake);
      setScore(s.score);
    } else {
      s.snake.pop();
    }
    draw();
  }, [draw]);

  const startGame = useCallback(() => {
    if (loopRef.current) clearInterval(loopRef.current);
    stateRef.current = initState();
    stateRef.current.food = randomFood(stateRef.current.snake);
    setScore(0);
    setPhase("playing");
    draw();
    loopRef.current = setInterval(tick, 110);
  }, [tick, draw]);

  const steer = useCallback((dir) => {
    const s = stateRef.current;
    if (!s) return;
    const map = { up:{x:0,y:-1}, down:{x:0,y:1}, left:{x:-1,y:0}, right:{x:1,y:0} };
    const nd = map[dir];
    if (!nd) return;
    if (nd.x === -s.dir.x && nd.y === -s.dir.y) return;
    s.nextDir = nd;
  }, []);

  useEffect(() => {
    draw();
    return () => { if (loopRef.current) clearInterval(loopRef.current); };
  }, [draw]);

  useEffect(() => {
    const onKey = (e) => {
      if (phase !== "playing") return;
      const map = { ArrowUp:"up", ArrowDown:"down", ArrowLeft:"left", ArrowRight:"right" };
      if (!map[e.key]) return;
      e.preventDefault();
      steer(map[e.key]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, steer]);

  const DPad = () => {
    const btn = (dir, label) => (
      <button
        onPointerDown={(e) => { e.preventDefault(); if (phase === "playing") steer(dir); }}
        className="font-pixel flex items-center justify-center select-none"
        style={{ width: 52, height: 52, background: "#1a1a1a", border: "2px solid #333", color: "var(--cream)", fontSize: 20, cursor: "pointer", touchAction: "none" }}>
        {label}
      </button>
    );
    return (
      <div style={{ display: "grid", gridTemplateColumns: "52px 52px 52px", gridTemplateRows: "52px 52px 52px", gap: 4, marginTop: 12 }}>
        <div />{btn("up", "↑")}<div />
        {btn("left", "←")}<div style={{ width: 52, height: 52, background: "#111", border: "2px solid #222" }} />{btn("right", "→")}
        <div />{btn("down", "↓")}<div />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full items-center justify-center gap-3" style={{ background: "#0d0d0d" }}>
      <div className="flex items-center gap-4">
        <button onClick={onBack}
          className="font-pixel text-[15px] px-3 py-1"
          style={{ color: "var(--cream)", border: "2px solid #444" }}>
          ← BACK
        </button>
        <div className="font-pixel text-[20px]" style={{ color: "var(--red)" }}>SNAKE.EXE</div>
        <div className="font-pixel text-[20px]" style={{ color: "var(--cream)" }}>SCORE: {score}</div>
      </div>

      <div className="relative" style={{ border: "3px solid var(--red)", boxShadow: "0 0 20px rgba(230,57,70,0.4)" }}>
        <canvas ref={canvasRef} width={COLS * CELL} height={ROWS * CELL} />
        {phase === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
            style={{ background: "rgba(0,0,0,0.88)" }}>
            <div className="font-pixel text-[28px]" style={{ color: "var(--red)" }}>SNAKE.EXE</div>
            <div className="font-pixel text-[15px]" style={{ color: "#888" }}>ARROW KEYS OR D-PAD</div>
            <button onClick={startGame}
              className="font-pixel text-[18px] px-8 py-2 mt-2"
              style={{ background: "var(--red)", color: "var(--black)", border: "3px solid var(--black)" }}>
              START
            </button>
          </div>
        )}
        {phase === "over" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
            style={{ background: "rgba(0,0,0,0.88)" }}>
            <div className="font-pixel text-[28px]" style={{ color: "var(--red)" }}>GAME OVER</div>
            <div className="font-pixel text-[20px]" style={{ color: "var(--cream)" }}>SCORE: {score}</div>
            <button onClick={startGame}
              className="font-pixel text-[18px] px-8 py-2 mt-2"
              style={{ background: "var(--red)", color: "var(--black)", border: "3px solid var(--black)" }}>
              PLAY AGAIN
            </button>
          </div>
        )}
      </div>

      <DPad />
    </div>
  );
}

// ─── Search ───────────────────────────────────────────────────────────────────
export default function SearchApp() {
  const [view,     setView]     = useState("home");
  const [inputVal, setInputVal] = useState("");
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const inputRef   = useRef(null);
  const topInputRef = useRef(null);

  const doSearch = async (q) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    if (trimmed.toLowerCase() === "snake") { setView("snake"); return; }
    setQuery(trimmed);
    setView("results");
    setLoading(true);
    setResults(null);
    try {
      const data = await searchWeb(trimmed);
      setResults(data);
    } catch {
      setResults({ abstract: "", results: [], error: true });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); doSearch(inputVal); };

  const handleLucky = () => {
    const q = inputVal.trim();
    if (!q) return;
    openExternal(`https://duckduckgo.com/?q=%21+${encodeURIComponent(q)}`);
  };

  const goHome = () => { setView("home"); setInputVal(""); setResults(null); };

  useEffect(() => {
    if (view === "home" && inputRef.current) inputRef.current.focus();
  }, [view]);

  if (view === "snake") return <SnakeGame onBack={goHome} />;

  const noResults = results && !results.abstract && (!results.results || results.results.length === 0);

  return (
    <div className="flex flex-col h-full" style={{ background: "#f8f4ef" }}>

      {/* ── Top bar (inlined — no sub-component to avoid remount bug) ── */}
      <div className="flex items-center gap-2 px-3 py-2 shrink-0"
        style={{ background: "var(--black)", borderBottom: "3px solid var(--red)" }}>
        {view === "results" && (
          <button onClick={goHome}
            className="font-pixel text-[15px] px-2 py-1"
            style={{ color: "var(--cream)", border: "2px solid #444", marginRight: 4, cursor: "pointer" }}>
            ←
          </button>
        )}
        <span className="font-pixel text-[13px] shrink-0" style={{ color: "var(--red)" }}>JRY://</span>
        {view === "results" ? (
          <form onSubmit={handleSubmit} className="flex flex-1 gap-2">
            <input
              ref={topInputRef}
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              className="flex-1 font-pixel text-[14px] px-2 py-1 outline-none"
              style={{ background: "#222", color: "var(--cream)", border: "2px solid #444" }}
            />
            <button type="submit"
              className="font-pixel text-[14px] px-3 py-1"
              style={{ background: "var(--red)", color: "var(--black)", border: "none", cursor: "pointer" }}>
              GO
            </button>
          </form>
        ) : (
          <span className="font-pixel text-[13px]" style={{ color: "#555" }}>SEARCH ENGINE v2.0</span>
        )}
      </div>

      {/* ── Home ── */}
      {view === "home" && (
        <>
          <div className="flex flex-col flex-1 items-center justify-center px-8 pb-8">
            <div className="mb-8 text-center select-none">
              <div className="font-display leading-none" style={{ fontSize: 68 }}>
                {"JRY SEARCH".split("").map((ch, i) => {
                  const colors = ["#4285f4","#ea4335","#fbbc05","#4285f4","#34a853","#ea4335","#4285f4","#fbbc05","#34a853","#ea4335"];
                  return ch === " "
                    ? <span key={i}>&nbsp;&nbsp;</span>
                    : <span key={i} style={{ color: colors[i] }}>{ch}</span>;
                })}
              </div>
              <div className="font-pixel text-[12px] mt-1" style={{ color: "#aaa" }}>Beta — Est. 2001 ™</div>
            </div>

            <form onSubmit={handleSubmit} className="w-full max-w-lg">
              <div className="flex items-center mb-4"
                style={{ border: "2px solid #ccc", background: "#fff", boxShadow: "2px 2px 0 #bbb" }}>
                <input
                  ref={inputRef}
                  value={inputVal}
                  onChange={e => setInputVal(e.target.value)}
                  placeholder='Search anything… or type "snake"'
                  className="flex-1 px-4 py-2.5 outline-none"
                  style={{ background: "transparent", color: "#333", fontFamily: "sans-serif", fontSize: 15 }}
                />
              </div>
              <div className="flex items-center justify-center gap-3">
                <button type="submit"
                  className="font-pixel text-[13px] px-5 py-2"
                  style={{ background: "#f1f3f4", border: "1px solid #ccc", color: "#333", boxShadow: "1px 1px 0 #aaa", cursor: "pointer" }}>
                  JRY SEARCH
                </button>
                <button type="button" onClick={handleLucky}
                  className="font-pixel text-[13px] px-5 py-2"
                  style={{ background: "#f1f3f4", border: "1px solid #ccc", color: "#333", boxShadow: "1px 1px 0 #aaa", cursor: "pointer" }}>
                  I'M FEELING LUCKY
                </button>
              </div>
            </form>

            <div className="mt-6 font-pixel text-[12px]" style={{ color: "#bbb" }}>
              tip: type "<span style={{ color: "#e63946" }}>snake</span>" and press search
            </div>
          </div>

          <div className="text-center py-2.5 font-pixel text-[12px] shrink-0"
            style={{ color: "#999", borderTop: "1px solid #e0e0e0" }}>
            © 2001 JRY CORP &nbsp;·&nbsp;
            <button onClick={() => setView("snake")} style={{ color: "#e63946", cursor: "pointer" }}>
              SNAKE.EXE
            </button>
          </div>
        </>
      )}

      {/* ── Results ── */}
      {view === "results" && (
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="font-pixel text-[15px] mt-10 text-center" style={{ color: "#888" }}>
              SEARCHING...
            </div>
          )}

          {/* 404 — no results */}
          {noResults && (
            <div className="flex flex-col items-center justify-center h-full gap-3 pb-10">
              <div className="font-pixel text-[72px] leading-none" style={{ color: "var(--red)" }}>404</div>
              <div className="font-pixel text-[18px]" style={{ color: "#333" }}>NO RESULTS FOUND</div>
              <div className="font-pixel text-[13px]" style={{ color: "#aaa" }}>"{query}" returned nothing. Try something else.</div>
              <button onClick={goHome}
                className="font-pixel text-[14px] px-6 py-2 mt-2"
                style={{ background: "var(--black)", color: "var(--cream)", border: "2px solid var(--black)", cursor: "pointer" }}>
                ← BACK TO SEARCH
              </button>
            </div>
          )}

          {results && !noResults && (
            <div style={{ maxWidth: 680 }}>
              <div className="font-pixel text-[12px] mb-5" style={{ color: "#888" }}>
                Results for "<span style={{ color: "#333" }}>{query}</span>"
              </div>

              {results.abstract && (
                <div className="mb-6 p-4"
                  style={{ background: "#fff", border: "1px solid #ddd", boxShadow: "2px 2px 0 #ccc" }}>
                  {results.image && (
                    <img src={results.image} alt="" className="float-right ml-4 mb-2"
                      style={{ width: 80, height: 80, objectFit: "cover", border: "1px solid #ddd" }} />
                  )}
                  <p style={{ color: "#333", fontFamily: "serif", fontSize: 14, lineHeight: 1.6 }}>
                    {results.abstract}
                  </p>
                  {results.abstract_url && (
                    <button onClick={() => openExternal(results.abstract_url)}
                      className="font-pixel text-[12px] mt-2 block"
                      style={{ color: "#1a0dab", cursor: "pointer" }}>
                      {results.abstract_source || "Source"} ↗
                    </button>
                  )}
                </div>
              )}

              {results.results?.map((r, i) => (
                <div key={i} className="mb-5">
                  <button onClick={() => openExternal(r.url)}
                    className="font-pixel text-[15px] block mb-0.5 text-left hover:underline"
                    style={{ color: "#1a0dab", cursor: "pointer" }}>
                    {r.title}
                  </button>
                  <div className="font-pixel text-[11px] mb-1" style={{ color: "#006621" }}>
                    {r.url?.replace(/^https?:\/\//, "").slice(0, 65)}
                  </div>
                  <p style={{ color: "#545454", fontFamily: "sans-serif", fontSize: 13, lineHeight: 1.5 }}>
                    {r.snippet?.slice(0, 200)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
